<template>
  <section class="mx-auto max-w-[1440px] space-y-6 px-4 py-8 sm:px-6 lg:px-8">
    <div v-if="loading" class="rounded-[28px] border border-dashed border-[#eadfd4] bg-white p-8 text-sm text-muted">
      Loading venue details...
    </div>

    <div v-else class="space-y-6">
      <section class="overflow-hidden rounded-[32px] border border-[#eadfd4] bg-white shadow-sm">
        <div class="border-b border-[#efe3d8] px-6 py-5 lg:px-8">
          <nav class="mb-6 flex flex-wrap items-center gap-3 text-sm text-muted">
            <button
              type="button"
              class="inline-flex items-center gap-2 rounded-full border border-[#e6d5c5] bg-white px-5 py-3 font-semibold text-ink shadow-sm transition hover:border-brand hover:text-brand"
              @click="goBack"
            >
              <i class="ri-arrow-left-line text-base"></i>
              <span>Back</span>
            </button>
            <span class="text-[#d2b9a3]">/</span>
            <span class="font-medium text-[#b78969]">Business</span>
            <span class="text-[#d2b9a3]">/</span>
            <span class="font-medium text-[#b78969]">{{ breadcrumbCategory }}</span>
            <span class="text-[#d2b9a3]">/</span>
            <span class="font-semibold text-ink">{{ venue.title || 'Venue' }}</span>
          </nav>

          <div class="flex flex-wrap items-center justify-between gap-4">
            <div>
              <h1 class="mt-2 font-serif text-[2rem] font-semibold leading-tight text-ink lg:text-[2.6rem]">{{ venue.title }}</h1>
              <p class="mt-3 text-[1.05rem] italic text-[#927660]">{{ venue.tagLine || 'Venue details available for your event invite.' }}</p>
            </div>
            <span class="rounded-full bg-[#fbf1e6] px-4 py-2 text-sm font-semibold text-brand">
              {{ venue.typeLabel }}
            </span>
          </div>
        </div>

        <div class="space-y-3 px-6 py-5 lg:px-8">
          <div class="mx-auto grid max-w-[980px] gap-2.5 sm:gap-3 lg:grid-cols-4 lg:grid-rows-2">
            <button
              type="button"
              class="relative min-h-[240px] overflow-hidden rounded-[28px] text-left sm:min-h-[260px] lg:col-span-2 lg:row-span-2 lg:h-[400px] lg:min-h-0"
              @click="openInviteGalleryById(heroGallery[0])"
            >
              <img
                v-if="heroGallery[0]"
                :src="heroGallery[0].src"
                :alt="venue.title"
                class="h-full w-full rounded-[28px] bg-[#f5efe8] object-cover"
                :class="{ 'object-contain p-5': heroGallery[0].isPlaceholder }"
              >
              <span v-if="heroGallery[0]?.isCover && !heroGallery[0]?.isPlaceholder" class="absolute left-4 top-4 rounded-full bg-white/95 px-3 py-1 text-[11px] font-semibold uppercase tracking-wide text-brand shadow-sm">
                Cover image
              </span>
            </button>

            <button
              v-for="(image, index) in heroGallery.slice(1, 5)"
              :key="`${image.id || 'hero-side'}-${image.src || index}-${index}`"
              type="button"
              class="relative min-h-[140px] overflow-hidden rounded-[24px] text-left sm:min-h-[160px] lg:h-[192px] lg:min-h-0"
              @click="openInviteGalleryById(image)"
            >
              <img
                :src="image.src"
                :alt="`${venue.title} ${index + 2}`"
                class="h-full w-full rounded-[24px] bg-[#f5efe8] object-cover"
                :class="{ 'object-contain p-4': image.isPlaceholder }"
              >
              <div
                v-if="image.overlayText"
                class="absolute inset-0 flex flex-col items-center justify-center bg-[#1f1208]/55 text-center text-white"
              >
                <span class="text-[1.45rem] font-semibold leading-none">{{ image.overlayText }}</span>
                <span class="mt-1.5 text-[10px] font-semibold uppercase tracking-[0.18em]">more photos</span>
              </div>
            </button>
          </div>

          <div class="rounded-[28px] border border-[#eadfd4] bg-[#fffdf9] p-5">
            <div class="flex flex-wrap items-center gap-x-4 gap-y-2 text-sm text-[#6f5948]">
              <span class="inline-flex items-center gap-2">
                <i class="ri-map-pin-2-fill text-[#d16c57]"></i>
                {{ venue.address }}
              </span>
              <span class="text-[#d8c8bb]">·</span>
              <span>{{ establishedLabel }}</span>
              <span v-if="venue.phone" class="text-[#d8c8bb]">·</span>
              <span v-if="venue.phone" class="inline-flex items-center gap-2">
                <i class="ri-phone-line text-brand"></i>
                {{ venue.phoneCode ? `${venue.phoneCode} ` : '' }}{{ venue.phone }}
              </span>
            </div>

            <p class="mt-6 text-[1.05rem] leading-8 text-[#2f241b]">
              {{ venue.about || 'Venue description is not available yet.' }}
            </p>

            <div v-if="venue.highlights?.length" class="mt-6">
              <p class="text-xs font-semibold uppercase tracking-[0.18em] text-[#b78969]">Highlights</p>
              <div class="mt-3 flex flex-wrap gap-2">
                <span v-for="item in venue.highlights" :key="item" class="rounded-full bg-[#f5eee8] px-3 py-1.5 text-sm font-semibold text-[#6d5542]">
                  {{ item }}
                </span>
              </div>
            </div>

            <div v-if="venue.inHouseServices?.length" class="mt-6">
              <p class="text-xs font-semibold uppercase tracking-[0.18em] text-[#b78969]">In-house services</p>
              <div class="mt-4 grid gap-3 md:grid-cols-2">
                <div v-for="item in venue.inHouseServices" :key="item" class="flex items-center gap-3 text-[1rem] text-[#2f241b]">
                  <span class="text-[#2e8a5d]">✓</span>
                  <span>{{ item }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section class="grid gap-6 xl:grid-cols-[minmax(0,1fr)_380px]">
        <div class="space-y-6">
          <section v-if="spaces?.length" class="rounded-[28px] border border-[#eadfd4] bg-white p-6 shadow-sm">
            <div>
              <p class="text-xs font-semibold uppercase tracking-[0.18em] text-[#b78969]">Spaces</p>
              <h2 class="mt-2 font-serif text-[1.9rem] font-semibold text-ink">Venue spaces</h2>
            </div>

            <div v-if="spaces?.length" class="mt-5">
              <div class="flex flex-wrap gap-3">
                <button
                  v-for="item in spaces"
                  :key="item.id"
                  type="button"
                  class="rounded-full border px-5 py-2.5 text-sm font-semibold transition"
                  :class="selectedSpaceId === item.id ? 'border-[#d9a441] bg-[#fff7e8] text-[#80550d] shadow-sm' : 'border-[#eadfd4] bg-white text-[#6f5948] hover:border-[#d9a441] hover:text-[#80550d]'"
                  @click="selectedSpaceId = item.id"
                >
                  {{ item.spaceIcon }} {{ item.name }}
                </button>
              </div>

              <article v-if="selectedSpace" class="mt-5 overflow-hidden rounded-[26px] border border-[#eadfd4] bg-[#fffdfa]">
                <div class="flex flex-col gap-5 border-b border-[#eadfd4] px-6 py-6 lg:flex-row lg:items-start lg:justify-between">
                  <div class="min-w-0">
                    <h3 class="font-serif text-[2rem] font-semibold text-[#1f1208]">{{ selectedSpace.name }}</h3>
                    <div class="mt-4 flex flex-wrap gap-2">
                      <span class="rounded-full bg-[#f6efe8] px-3 py-1.5 text-sm font-semibold text-[#7a624e]">{{ selectedSpace.settingIcon }} {{ selectedSpace.setting || 'Indoor' }}</span>
                      <span class="rounded-full bg-[#f6efe8] px-3 py-1.5 text-sm font-semibold text-[#7a624e]">{{ selectedSpace.spaceTypeIcon }} {{ selectedSpace.spaceType || 'Space' }}</span>
                      <span v-if="selectedSpace.areaSqft" class="rounded-full bg-[#f6efe8] px-3 py-1.5 text-sm font-semibold text-[#7a624e]">📐 {{ selectedSpace.areaSqft }} sqft</span>
                    </div>
                  </div>

                  <div class="text-left lg:text-right">
                    <p class="text-[2rem] font-semibold leading-none text-[#1f1208]">{{ selectedSpace.displayMinPrice }}</p>
                    <p v-if="selectedSpace.displayMaxPrice" class="mt-2 text-base text-[#8b715e]">to {{ selectedSpace.displayMaxPrice }} per event</p>
                  </div>
                </div>

                <div class="px-6 py-6">
                  <p class="text-[1.05rem] leading-8 text-[#2f241b]">
                    {{ selectedSpace.description || 'No description added.' }}
                  </p>

                  <div v-if="selectedSpace.seatingItems?.length" class="mt-8">
                    <p class="text-xs font-semibold uppercase tracking-[0.18em] text-[#b78969]">Seating configurations</p>
                    <div class="mt-4 grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
                      <article v-for="item in selectedSpace.seatingItems || []" :key="`${selectedSpace.id}-${item.label}`" class="rounded-[18px] border border-[#eadfd4] bg-white px-4 py-4">
                        <div class="flex items-end justify-between gap-3">
                          <p class="text-[1rem] font-medium leading-6 text-[#2f241b]">{{ item.label }}</p>
                          <p class="text-lg font-semibold text-[#1f1208]">{{ item.capacity }}</p>
                        </div>
                      </article>
                    </div>
                  </div>

                  <div class="mt-8 grid gap-8 lg:grid-cols-2">
                    <div>
                      <p class="text-xs font-semibold uppercase tracking-[0.18em] text-[#b78969]">Included amenities</p>
                      <div class="mt-4 space-y-3">
                        <div v-for="item in selectedSpace.includedAmenities || []" :key="`${selectedSpace.id}-amenity-${item}`" class="flex items-start justify-between gap-3 text-[1rem] text-[#2f241b]">
                          <span class="inline-flex items-center gap-3">
                            <span class="text-[#2e8a5d]">✓</span>
                            <span>{{ item }}</span>
                          </span>
                        </div>
                        <p v-if="!selectedSpace.includedAmenities?.length" class="text-sm text-muted">No included amenities added yet.</p>
                      </div>
                    </div>

                    <div>
                      <p class="text-xs font-semibold uppercase tracking-[0.18em] text-[#b78969]">Equipment</p>
                      <div class="mt-4 space-y-3">
                        <div v-for="item in selectedSpace.equipmentItems || []" :key="`${selectedSpace.id}-equipment-${item.label}`" class="flex items-start justify-between gap-3 text-[1rem] text-[#2f241b]">
                          <span class="inline-flex items-center gap-3">
                            <span :class="item.included ? 'text-[#2e8a5d]' : 'text-[#c58a27]'">{{ item.included ? '✓' : '+' }}</span>
                            <span>{{ item.label }}</span>
                          </span>
                          <span :class="item.included ? 'text-[#2e8a5d]' : 'text-[#c58a27]'" class="text-sm font-semibold">
                            {{ item.included ? 'Included' : 'Optional' }}
                          </span>
                        </div>
                        <p v-if="!selectedSpace.equipmentItems?.length" class="text-sm text-muted">No equipment details added yet.</p>
                      </div>
                    </div>
                  </div>
                </div>
              </article>
            </div>
          </section>

          <section v-if="packages?.length" class="rounded-[28px] border border-[#eadfd4] bg-white p-6 shadow-sm">
            <div>
              <p class="text-xs font-semibold uppercase tracking-[0.18em] text-[#b78969]">Packages</p>
              <h2 class="mt-2 font-serif text-[1.9rem] font-semibold text-ink">Venue packages</h2>
            </div>

            <div v-if="packages?.length" class="mt-5 grid gap-4 md:grid-cols-2">
              <article v-for="item in packages" :key="item.id" class="rounded-[24px] border border-[#eadfd4] bg-[#fffaf5] p-5">
                <div class="flex items-start justify-between gap-4">
                  <div>
                    <h3 class="text-lg font-semibold text-ink">{{ item.name }}</h3>
                    <div class="mt-2 flex flex-wrap gap-2 text-xs text-[#7d634e]">
                      <span class="rounded-full bg-white px-3 py-1 font-semibold ring-1 ring-[#eadfd4]">{{ item.priceLabel }}</span>
                      <span v-if="item.maxGuests" class="rounded-full bg-white px-3 py-1 font-semibold ring-1 ring-[#eadfd4]">{{ item.maxGuests }} guests</span>
                      <span v-if="item.validHours" class="rounded-full bg-white px-3 py-1 font-semibold ring-1 ring-[#eadfd4]">{{ item.validHours }} hrs</span>
                    </div>
                  </div>
                </div>
                <p class="mt-4 text-sm leading-7 text-[#4b3b2f]">{{ item.description || 'No description added.' }}</p>
                <div v-if="item.includedServices?.length" class="mt-4 flex flex-wrap gap-2">
                  <span v-for="service in item.includedServices || []" :key="service" class="rounded-full border border-[#eadfd4] bg-white px-3 py-1 text-xs font-semibold text-[#6f5948]">
                    {{ service }}
                  </span>
                </div>
              </article>
            </div>
          </section>

          <section class="rounded-[28px] border border-[#eadfd4] bg-white p-6 shadow-sm">
            <div>
              <p class="text-xs font-semibold uppercase tracking-[0.18em] text-[#b78969]">Transportation</p>
              <h2 class="mt-2 font-serif text-[1.9rem] font-semibold text-ink">Transportation & venue timings</h2>
            </div>

            <div class="mt-5 grid gap-8 md:grid-cols-2">
              <div>
                <p class="text-xs font-semibold uppercase tracking-[0.18em] text-[#b78969]">Venue facts</p>
                <div class="mt-5 space-y-5">
                  <article class="flex items-start gap-4">
                    <span class="mt-0.5 text-[1.35rem]">🛏️</span>
                    <div>
                      <p class="text-xs font-semibold uppercase tracking-[0.12em] text-[#b78969]">Rooms</p>
                      <p class="mt-1 text-[1.02rem] leading-7 text-[#2f241b]">{{ venue.noOfRooms || 'Room count not added yet.' }}</p>
                    </div>
                  </article>
                </div>
              </div>

              <div>
                <p class="text-xs font-semibold uppercase tracking-[0.18em] text-[#b78969]">Venue timing</p>
                <div class="mt-5 overflow-hidden rounded-[18px] border border-[#eadfd4]">
                  <div class="grid grid-cols-[44px_minmax(0,1fr)_80px_80px] gap-2 border-b border-[#eadfd4] bg-[#fbf6f0] px-4 py-3 text-xs font-semibold uppercase tracking-[0.12em] text-[#b78969]">
                    <span></span>
                    <span>Timing</span>
                    <span>Start</span>
                    <span>End</span>
                  </div>
                  <div v-for="item in venueTimingRows" :key="item.key" class="grid grid-cols-[44px_minmax(0,1fr)_80px_80px] gap-2 border-b border-[#eadfd4] px-4 py-3 text-[1rem] text-[#2f241b] last:border-b-0">
                    <span :class="item.available ? 'border-brand bg-brand text-white' : 'border-[#d8c8b9] bg-white text-transparent'" class="inline-flex h-5 w-5 items-center justify-center rounded border text-xs font-bold">✓</span>
                    <span>{{ item.label }}</span>
                    <span>{{ item.start || '-' }}</span>
                    <span>{{ item.end || '-' }}</span>
                  </div>
                </div>
              </div>
            </div>

            <div v-if="hasTransportationDetails" class="mt-6">
              <p class="text-xs font-semibold uppercase tracking-[0.18em] text-[#b78969]">Transportation</p>
              <div v-if="nearbyAreasList.length" class="mt-4 overflow-hidden rounded-[18px] border border-[#eadfd4]">
                <div v-for="item in nearbyAreasList" :key="item" class="flex items-center gap-3 border-b border-[#eadfd4] px-4 py-3 text-[1rem] text-[#2f241b] last:border-b-0">
                  <i class="ri-map-pin-2-fill text-[#d16c57]"></i>
                  <span>{{ item }}</span>
                </div>
              </div>
              <a
                v-if="transportationMapLink"
                :href="transportationMapLink"
                target="_blank"
                rel="noreferrer"
                class="mt-4 inline-flex items-center gap-2 rounded-full border border-[#eadfd4] bg-[#fffaf5] px-4 py-2 text-sm font-semibold text-brand transition hover:border-brand"
              >
                <i class="ri-road-map-line"></i>
                Open saved Google Maps link
              </a>
            </div>
          </section>

          <section class="rounded-[28px] border border-[#eadfd4] bg-white p-6 shadow-sm">
            <div>
              <p class="text-xs font-semibold uppercase tracking-[0.18em] text-[#b78969]">Parking</p>
              <h2 class="mt-2 font-serif text-[1.9rem] font-semibold text-ink">Parking & venue facilities</h2>
            </div>

            <div v-if="parkingDetailRows.length" class="mt-6">
              <p class="text-xs font-semibold uppercase tracking-[0.18em] text-[#b78969]">Car & Valet Parking</p>
              <div class="mt-4 overflow-hidden rounded-[18px] border border-[#eadfd4]">
                <div v-for="item in parkingDetailRows" :key="item.label" class="grid gap-3 border-b border-[#eadfd4] px-4 py-3 last:border-b-0 sm:grid-cols-[24px_minmax(0,1fr)_auto]">
                  <span :class="item.available ? 'text-[#2e8a5d]' : 'text-[#d63f2f]'" class="text-[1.2rem] leading-none">
                    <i :class="item.iconClass"></i>
                  </span>
                  <span class="text-[1rem] text-[#2f241b]">{{ item.label }}</span>
                  <span class="font-semibold text-ink">{{ item.value }}</span>
                </div>
              </div>
            </div>

            <div v-if="additionalFacilitiesList.length" class="mt-6">
              <p class="text-xs font-semibold uppercase tracking-[0.18em] text-[#b78969]">Additional facilities</p>
              <div class="mt-4 overflow-hidden rounded-[18px] border border-[#eadfd4]">
                <div v-for="item in additionalFacilitiesList" :key="item.label" class="grid gap-3 border-b border-[#eadfd4] px-4 py-3 last:border-b-0 sm:grid-cols-[24px_minmax(0,1fr)]">
                  <span :class="item.available ? 'text-[#2e8a5d]' : 'text-[#d63f2f]'" class="text-[1.2rem] leading-none">
                    <i :class="item.iconClass"></i>
                  </span>
                  <span class="text-[1rem] text-[#2f241b]">{{ item.label }}</span>
                </div>
              </div>
            </div>
          </section>

          <section class="rounded-[28px] border border-[#eadfd4] bg-white p-6 shadow-sm">
            <div>
              <p class="text-xs font-semibold uppercase tracking-[0.18em] text-[#b78969]">Policies</p>
              <h2 class="mt-2 font-serif text-[1.9rem] font-semibold text-ink">Venue policies</h2>
            </div>

            <div class="mt-6 grid gap-8 md:grid-cols-2">
              <div>
                <p class="text-xs font-semibold uppercase tracking-[0.18em] text-[#b78969]">Venue rules</p>
                <div class="mt-5 space-y-5">
                  <article v-for="item in policyRuleItems" :key="item.label" class="flex items-start gap-4">
                    <span class="mt-0.5 text-[1.35rem]">{{ item.icon }}</span>
                    <div>
                      <p class="text-xs font-semibold uppercase tracking-[0.12em] text-[#b78969]">{{ item.label }}</p>
                      <div v-if="item.rows?.length" class="mt-2 overflow-hidden rounded-[18px] border border-[#eadfd4]">
                        <div v-for="row in item.rows" :key="row.label" class="grid gap-3 border-b border-[#eadfd4] px-4 py-3 last:border-b-0 sm:grid-cols-[24px_minmax(0,1fr)]">
                          <span :class="row.available ? 'text-[#2e8a5d]' : 'text-[#d63f2f]'" class="text-[1.2rem] leading-none">
                            <i :class="row.iconClass"></i>
                          </span>
                          <span class="text-[1rem] text-[#2f241b]">{{ row.label }}</span>
                        </div>
                      </div>
                      <p v-else class="mt-1 text-[1.02rem] leading-7 text-[#2f241b]">{{ item.value }}</p>
                    </div>
                  </article>
                </div>
              </div>

              <div>
                <p class="text-xs font-semibold uppercase tracking-[0.18em] text-[#b78969]">Booking terms</p>
                <div class="mt-5 space-y-5">
                  <article v-for="item in bookingTermItems" :key="item.label" class="flex items-start gap-4">
                    <span class="mt-0.5 text-[1.35rem]">{{ item.icon }}</span>
                    <div>
                      <p class="text-xs font-semibold uppercase tracking-[0.12em] text-[#b78969]">{{ item.label }}</p>
                      <p class="mt-1 text-[1.02rem] leading-7 text-[#2f241b]">{{ item.value }}</p>
                    </div>
                  </article>
                </div>
              </div>
            </div>
          </section>

          <section class="rounded-[28px] border border-[#eadfd4] bg-white p-6 shadow-sm">
            <div>
              <p class="text-xs font-semibold uppercase tracking-[0.18em] text-[#b78969]">Catering & Food Restrictions</p>
              <h2 class="mt-2 font-serif text-[1.9rem] font-semibold text-ink">Catering & food restrictions</h2>
              <div v-if="cateringRestrictionRows.length" class="mt-5 overflow-hidden rounded-[18px] border border-[#eadfd4]">
                <div v-for="row in cateringRestrictionRows" :key="row.label" class="grid gap-3 border-b border-[#eadfd4] px-4 py-3 last:border-b-0 sm:grid-cols-[24px_minmax(0,1fr)]">
                  <span :class="row.available ? 'text-[#2e8a5d]' : 'text-[#d63f2f]'" class="text-[1.2rem] leading-none">
                    <i :class="row.iconClass"></i>
                  </span>
                  <span class="text-[1rem] text-[#2f241b]">{{ row.label }}</span>
                </div>
              </div>
              <p class="mt-4 text-[1rem] leading-7 text-[#2f241b]">
                Below listed prices are <strong>excluding all taxes.</strong>
              </p>
              <p class="text-[1rem] leading-7 text-[#2f241b]">
                Menu prices are subject to change, will be varies depending on guest requirement and number of people. Please contact manager for more details.
              </p>
            </div>

            <div v-if="menuPricingItems.length" class="mt-5 overflow-hidden rounded-[22px] border border-[#eadfd4]">
              <table class="min-w-full border-collapse bg-white text-left">
                <thead class="bg-[#fbf6f0]">
                  <tr>
                    <th class="border-b border-r border-[#eadfd4] px-4 py-3 text-sm font-semibold text-ink">Menu Name</th>
                    <th class="border-b border-[#eadfd4] px-4 py-3 text-sm font-semibold text-ink">{{ menuPricingUnitLabel }}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="item in menuPricingItems" :key="item.name">
                    <td class="border-b border-r border-[#eadfd4] px-4 py-3 text-[1rem] text-[#2f241b]">
                      <span class="inline-flex items-center gap-2">
                        <span class="h-3.5 w-3.5 rounded-[4px] bg-white p-[2px]" :class="item.swatchBorderClass">
                          <span class="block h-full w-full rounded-[2px]" :class="item.swatchClass"></span>
                        </span>
                        {{ item.name }}
                      </span>
                    </td>
                    <td class="border-b border-[#eadfd4] px-4 py-3 text-[1rem] font-medium text-[#2f241b]">{{ item.price }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p v-else class="mt-5 rounded-[18px] border border-dashed border-[#eadfd4] bg-[#fffaf5] px-4 py-3 text-sm text-muted">
              Menu pricing not added yet.
            </p>
          </section>

          <section class="rounded-[28px] border border-[#eadfd4] bg-white p-6 shadow-sm">
            <div>
              <p class="text-xs font-semibold uppercase tracking-[0.18em] text-[#b78969]">Location</p>
              <h2 class="mt-2 font-serif text-[1.9rem] font-semibold text-ink">{{ venue.address || 'Venue location' }}</h2>
            </div>

            <div class="mt-5 overflow-hidden rounded-[24px] border border-[#d8e7ef] bg-[#dff0fa]">
              <div
                class="relative flex min-h-[220px] items-center justify-center px-6 py-10 text-center"
                style="background-image: linear-gradient(to right, rgba(130,160,180,0.14) 1px, transparent 1px), linear-gradient(to bottom, rgba(130,160,180,0.14) 1px, transparent 1px); background-size: 112px 100%, 100% 34px;"
              >
                <div class="absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(255,255,255,0.22),transparent_58%)]"></div>
                <div class="relative z-10 flex flex-col items-center">
                  <div class="relative">
                    <span class="block h-5 w-5 rounded-full bg-[#ff5a49] shadow-[0_6px_14px_rgba(255,90,73,0.28)]"></span>
                    <span class="absolute left-1/2 top-4 h-8 w-[3px] -translate-x-1/2 rounded-full bg-[#4a89a8]"></span>
                  </div>
                  <p class="mt-10 font-serif text-[1.35rem] font-semibold text-[#2f241b]">{{ venue.address || 'Venue location not added yet.' }}</p>
                  <p class="mt-1 text-[1rem] text-[#5b6c76]">{{ venue.address || 'Hyderabad, Telangana' }}</p>
                  <a
                    href="#"
                    class="mt-5 inline-flex items-center gap-2 rounded-full bg-[#c99a22] px-5 py-3 text-sm font-semibold text-white transition hover:bg-[#b88817]"
                  >
                    Open in Google Maps
                    <i class="ri-arrow-right-up-line"></i>
                  </a>
                </div>
              </div>
            </div>
          </section>
        </div>

        <aside class="self-start xl:sticky xl:top-24">
          <section class="rounded-[28px] border border-[#eadfd4] bg-white p-6 shadow-sm">
            <p class="text-xs font-semibold uppercase tracking-[0.18em] text-[#b78969]">Enquiry</p>
            <h2 class="mt-2 font-serif text-[1.75rem] font-semibold text-ink">Ask about this venue</h2>
            <p class="mt-2 text-sm leading-7 text-muted">Share your event basics and venue requirements. We will submit your enquiry for this venue.</p>

            <form class="mt-5 space-y-4" @submit.prevent="submitVenueEnquiry">
              <label class="block text-xs font-semibold uppercase tracking-wide text-[#5b4638]">
                Full name
                <input v-model.trim="enquiryForm.name" type="text" class="mt-2 w-full rounded-2xl border border-[#e8ddd1] px-4 py-3 text-sm normal-case font-normal focus:border-brand focus:outline-none">
              </label>

              <label class="block text-xs font-semibold uppercase tracking-wide text-[#5b4638]">
                Email
                <input v-model.trim="enquiryForm.email" type="email" class="mt-2 w-full rounded-2xl border border-[#e8ddd1] px-4 py-3 text-sm normal-case font-normal focus:border-brand focus:outline-none">
              </label>

              <label class="block text-xs font-semibold uppercase tracking-wide text-[#5b4638]">
                Phone
                <input v-model.trim="enquiryForm.phone" type="tel" inputmode="numeric" class="mt-2 w-full rounded-2xl border border-[#e8ddd1] px-4 py-3 text-sm normal-case font-normal focus:border-brand focus:outline-none">
              </label>

              <div class="grid gap-4 sm:grid-cols-2">
                <label class="block text-xs font-semibold uppercase tracking-wide text-[#5b4638]">
                  Event date
                  <input v-model="enquiryForm.eventDate" type="date" class="mt-2 w-full rounded-2xl border border-[#e8ddd1] px-4 py-3 text-sm normal-case font-normal focus:border-brand focus:outline-none">
                </label>

                <label class="block text-xs font-semibold uppercase tracking-wide text-[#5b4638]">
                  Guest count
                  <input v-model.trim="enquiryForm.guestCount" type="text" inputmode="numeric" class="mt-2 w-full rounded-2xl border border-[#e8ddd1] px-4 py-3 text-sm normal-case font-normal focus:border-brand focus:outline-none">
                </label>
              </div>

              <label class="block text-xs font-semibold uppercase tracking-wide text-[#5b4638]">
                Requirements
                <textarea v-model.trim="enquiryForm.details" rows="5" class="mt-2 w-full rounded-2xl border border-[#e8ddd1] px-4 py-3 text-sm normal-case font-normal focus:border-brand focus:outline-none" placeholder="Tell us about your event type, budget range, seating needs, and any must-have requirements."></textarea>
              </label>

              <p v-if="enquiryError" class="rounded-2xl border border-red-100 bg-red-50 px-4 py-3 text-sm font-medium text-red-700">
                {{ enquiryError }}
              </p>
              <p v-if="enquirySuccess" class="rounded-2xl border border-emerald-100 bg-emerald-50 px-4 py-3 text-sm font-medium text-emerald-700">
                {{ enquirySuccess }}
              </p>

              <button
                type="submit"
                :disabled="enquiryLoading"
                class="w-full rounded-full bg-brand px-4 py-3 text-sm font-semibold text-white transition hover:bg-brandDark disabled:cursor-not-allowed disabled:opacity-60"
              >
                {{ enquiryLoading ? 'Sending enquiry...' : 'Send enquiry' }}
              </button>
            </form>
          </section>
        </aside>
      </section>
    </div>
  </section>
</template>

<script setup>
import { computed, onBeforeUnmount, onMounted, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import lightGallery from 'lightgallery'
import lgThumbnail from 'lightgallery/plugins/thumbnail'
import lgZoom from 'lightgallery/plugins/zoom'
import api from '@/services/user/api'
import { getAuthUser } from '@/services/auth'

const route = useRoute()
const router = useRouter()

const loading = ref(false)
const error = ref('')
const venue = ref({
  id: '',
  title: '',
  tagLine: '',
  about: '',
  address: '',
  phone: '',
  phoneCode: '',
  email: '',
  website: '',
  typeLabel: 'Venue',
  yearEstablished: '',
  highlights: [],
  inHouseServices: [],
  additionalFacilities: [],
  additionalFacilitiesStatuses: [],
  noOfRooms: '',
  parkingAvailable: '',
  valetParking: '',
  parkingVehicleCount: '',
  morningTimingFrom: '',
  morningTimingTo: '',
  eveningTimingFrom: '',
  eveningTimingTo: '',
  sessions: [],
  nearbyAreas: [],
  transportationMapLink: ''
})
const gallery = ref([])
const spaces = ref([])
const selectedSpaceId = ref('')
const packages = ref([])
const inviteGalleryLightbox = ref(null)
const authUser = getAuthUser() || {}
const enquiryLoading = ref(false)
const enquiryError = ref('')
const enquirySuccess = ref('')
const enquiryForm = ref({
  name: authUser?.name || authUser?.s_name || '',
  email: authUser?.email || authUser?.s_email || '',
  phone: authUser?.phone || authUser?.s_phone || '',
  eventDate: '',
  guestCount: '',
  details: ''
})
const policies = ref({
  inHouseCatering: '',
  outsideCatering: '',
  cateringFoodType: '',
  cateringPriceUnit: '',
  pricingBasisCount: '',
  nonVegPricingBasisCount: '',
  decorPolicy: '',
  musicPolicy: '',
  alcoholInhouse: '',
  alcoholOutside: '',
  minGuests: '',
  maxGuests: '',
  decorTime: '',
  cleanTime: '',
  noiseMusic: '',
  otherHouseRules: '',
  cancelTerms: '',
  depositPercent: ''
})
const noImagesPlaceholderSrc = '/assets/icons/no-images.svg'

const splitToArray = (value) =>
  String(value || '')
    .split(',')
    .map((item) => item.trim())
    .filter(Boolean)

const splitSessionValues = (value) => String(value || '').split(',').map((entry) => entry.trim())

const getFirstValue = (...values) => values.find((value) => String(value ?? '').trim() !== '') ?? ''

const normalizeVenueSessionTypeValue = (value) => {
  const normalized = String(value || '').trim().toLowerCase()
  if (!normalized) return ''
  if (normalized.includes('request')) return 'on-request'
  if (normalized.includes('dinner') || normalized.includes('night') || normalized.includes('evening')) return 'evening'
  if (normalized.includes('morning') || normalized.includes('lunch') || normalized.includes('afternoon')) return 'morning'
  return normalized
}

const buildVenueTimingSessions = (item = {}) => {
  const types = splitSessionValues(item?.session_type)
  const fromTimes = splitSessionValues(item?.session_from_time)
  const toTimes = splitSessionValues(item?.session_to_time)
  const total = Math.max(types.length, fromTimes.length, toTimes.length, 0)

  const sessions = Array.from({ length: total }, (_, index) => ({
    type: normalizeVenueSessionTypeValue(types[index]),
    fromTime: getFirstValue(fromTimes[index], normalizeVenueSessionTypeValue(types[index]) === 'morning' ? item?.session_from_time_mrng : '', normalizeVenueSessionTypeValue(types[index]) === 'evening' ? item?.session_from_time_night : ''),
    toTime: getFirstValue(toTimes[index], normalizeVenueSessionTypeValue(types[index]) === 'morning' ? item?.session_to_time_mrng : '', normalizeVenueSessionTypeValue(types[index]) === 'evening' ? item?.session_to_time_night : '')
  })).filter((session) => session.type || session.fromTime || session.toTime)

  if ((item?.session_from_time_mrng || item?.session_to_time_mrng) && !sessions.some((session) => session.type === 'morning')) {
    sessions.push({ type: 'morning', fromTime: item.session_from_time_mrng || '', toTime: item.session_to_time_mrng || '' })
  }
  if ((item?.session_from_time_night || item?.session_to_time_night) && !sessions.some((session) => session.type === 'evening')) {
    sessions.push({ type: 'evening', fromTime: item.session_from_time_night || '', toTime: item.session_to_time_night || '' })
  }
  if (isYesValue(item?.session_on_request) && !sessions.some((session) => session.type === 'on-request')) {
    sessions.push({ type: 'on-request', fromTime: '', toTime: '' })
  }

  return sessions
}

const formatCurrency = (value) => {
  const numeric = Number(String(value || '').replace(/[^\d.]/g, ''))
  return Number.isFinite(numeric) && numeric > 0 ? `Rs. ${numeric.toLocaleString('en-IN')}` : ''
}

const formatPriceRange = (minPrice, maxPrice) => {
  const minValue = formatCurrency(minPrice)
  const maxValue = formatCurrency(maxPrice)
  if (minValue && maxValue) return `${minValue} - ${maxValue}`
  return minValue || maxValue || 'Price on request'
}

const formatYesNo = (value) => {
  const normalized = String(value || '').trim().toLowerCase()
  if (!normalized) return ''
  if (['1', 'yes', 'true'].includes(normalized)) return 'Yes'
  if (['0', 'no', 'false'].includes(normalized)) return 'No'
  return String(value || '').trim()
}

const isYesValue = (value) => formatYesNo(value).toLowerCase() === 'yes'

const getStatusState = (value, fallback = null) => {
  const normalized = String(value ?? '').trim().toLowerCase()
  if (!normalized) return fallback
  if (['0', 'no', 'false', 'off'].includes(normalized) || /not available|not provided|unavailable|not allowed|do not|no\b/.test(normalized)) return false
  if (['1', 'yes', 'true', 'on'].includes(normalized) || /available|allowed|provided|permit|partner|panel|approved|own kitchen|welcome/.test(normalized)) return true
  return fallback
}

const getStatusIconClass = (available) => available ? 'ri-checkbox-circle-fill' : 'ri-close-circle-fill'

const formatStatusPolicyValue = (value, label) => {
  const available = getStatusState(value)
  const text = String(value || '').trim()
  if (available === null && !text) return ''
  return `${label}${text && text !== label ? `: ${text}` : ''}`
}

const formatStatusPolicyRow = (value, label) => {
  const available = getStatusState(value)
  if (available === null) return null
  return {
    label,
    iconClass: getStatusIconClass(available),
    available
  }
}

const parseBothFoodPricing = (value) => {
  const parsed = {
    veg: '',
    nonVeg: ''
  }

  String(value || '').split('||').map((item) => item.trim()).filter(Boolean).forEach((part) => {
    const [rawKey, rawValue] = part.split('::')
    const key = String(rawKey || '').trim().toLowerCase()
    const price = String(rawValue || '').trim()
    if (key === 'veg') parsed.veg = price
    if (key === 'nonveg') parsed.nonVeg = price
  })

  return parsed
}

const inferSpaceIcon = (value) => {
  const normalized = String(value || '').trim().toLowerCase()
  if (!normalized) return '🏛️'
  if (normalized.includes('lawn') || normalized.includes('garden')) return '🌿'
  if (normalized.includes('terrace') || normalized.includes('rooftop')) return '⛱️'
  if (normalized.includes('pool')) return '🏊'
  if (normalized.includes('outdoor')) return '☀️'
  if (normalized.includes('indoor')) return '🏠'
  if (normalized.includes('hall') || normalized.includes('ballroom')) return '🏛️'
  return '🏛️'
}

const normalizeSpace = (item = {}, index = 0) => {
  const configurations = splitToArray(item?.seat_config || item?.seatConfig)
  const capacities = splitToArray(item?.seat_capacity || item?.seatCapacity)
  const amenities = splitToArray(item?.amenities)
  const spaceType = item?.space_type || item?.spaceType || item?.type_name || item?.type || ''
  const setting = item?.setting || 'Indoor'
  const seatingItems = configurations.map((label, configIndex) => ({
    label,
    capacity: capacities[configIndex] || 'N/A'
  }))
  const includedAmenities = amenities.slice(0, 6)
  const equipmentItems = amenities.slice(6).map((label) => ({
    label,
    included: true
  }))

  return {
    id: String(item?.space_id || item?.id || `space-${index}`),
    name: item?.name || 'Untitled space',
    spaceType,
    setting,
    spaceIcon: inferSpaceIcon(spaceType || item?.name || ''),
    spaceTypeIcon: inferSpaceIcon(spaceType || ''),
    settingIcon: inferSpaceIcon(setting),
    areaSqft: item?.area_sqft || item?.areaSqft || '',
    description: item?.description || '',
    minPrice: item?.min_price || item?.minPrice || '',
    maxPrice: item?.max_price || item?.maxPrice || '',
    displayMinPrice: formatCurrency(item?.min_price || item?.minPrice) || 'Price on request',
    displayMaxPrice: formatCurrency(item?.max_price || item?.maxPrice) || '',
    priceLabel: formatPriceRange(item?.min_price || item?.minPrice, item?.max_price || item?.maxPrice),
    seatingItems,
    includedAmenities,
    equipmentItems,
    seatingSummary: configurations.map((label, configIndex) => {
      const capacity = capacities[configIndex]
      return capacity ? `${label}: ${capacity}` : label
    })
  }
}

const normalizeGallery = (items = []) => {
  const source = Array.isArray(items) ? items : [items]
  return source
    .flatMap((item, itemIndex) => {
      if (!item) return []
      if (typeof item === 'string') {
        return [{
          id: `gallery-${itemIndex}-${item}`,
          src: String(item).trim(),
          isCover: false
        }]
      }
      const imageKeys = ['image1', 'image2', 'image3', 'image4', 'image', 'image_url', 'url', 'path', 'img', 'src']
      const entryId = String(item?.id || item?.gallery_id || item?.venue_gallery_id || item?.img_id || '')
      return imageKeys
        .map((key, keyIndex) => {
          const src = String(item?.[key] || '').trim()
          if (!src) return null
          return {
            id: entryId || `gallery-${itemIndex}-${keyIndex}-${src}`,
            src,
            isCover: String(item?.is_cover || item?.isCover || '0') === '1'
          }
        })
        .filter(Boolean)
    })
    .sort((left, right) => Number(right.isCover) - Number(left.isCover))
}

const placeholderGallery = Array.from({ length: 5 }).map((_, index) => ({
  id: `placeholder-${index}`,
  src: noImagesPlaceholderSrc,
  tagName: 'No image available',
  isCover: false,
  isPlaceholder: true
}))

const lightboxGalleryImages = computed(() => {
  const sortedGallery = [...gallery.value].sort((left, right) => Number(right.isCover) - Number(left.isCover))
  const baseTiles = sortedGallery.slice(0, 5)
  const missing = Math.max(0, 5 - baseTiles.length)
  const filledTiles = [...baseTiles, ...placeholderGallery.slice(0, missing)].map((item, index) => ({
    ...item,
    id: item.id || `preview-tile-${index}`,
    overlayText: ''
  }))

  if (!filledTiles.length) {
    return placeholderGallery.map((item, index) => ({
      ...item,
      id: item.id || `preview-tile-${index}`,
      overlayText: ''
    }))
  }

  const hiddenCount = Math.max(0, sortedGallery.length - 5)
  if (filledTiles[4]) {
    filledTiles[4] = {
      ...filledTiles[4],
      overlayText: hiddenCount ? `+${hiddenCount}` : ''
    }
  }

  return filledTiles
})

const heroGallery = computed(() => lightboxGalleryImages.value.slice(0, 5))
const establishedLabel = computed(() => venue.value.yearEstablished ? `Est. ${venue.value.yearEstablished}` : 'Year not specified')
const additionalFacilitiesList = computed(() => {
  const items = Array.isArray(venue.value.additionalFacilities) ? venue.value.additionalFacilities : []
  const statuses = Array.isArray(venue.value.additionalFacilitiesStatuses) ? venue.value.additionalFacilitiesStatuses : []
  return items.map((item, index) => {
    const available = getStatusState(statuses[index], getStatusState(item, true))
    return {
      label: item,
      available,
      iconClass: getStatusIconClass(available)
    }
  }).filter((item) => item.label)
})
const nearbyAreasList = computed(() => venue.value.nearbyAreas.filter(Boolean))
const transportationMapLink = computed(() => String(venue.value.transportationMapLink || '').trim())
const parkingDetailRows = computed(() => {
  const carParking = formatYesNo(venue.value.parkingAvailable)
  const valetParking = formatYesNo(venue.value.valetParking)
  const vehicleCount = String(venue.value.parkingVehicleCount || '').trim()
  const rows = []

  if (carParking) {
    const available = isYesValue(venue.value.parkingAvailable)
    rows.push({ label: 'Car Parking available', value: carParking, available, iconClass: getStatusIconClass(available) })
  }
  if (isYesValue(venue.value.parkingAvailable) && vehicleCount) {
    rows.push({ label: 'Car parking available for vehicle count', value: vehicleCount, available: true, iconClass: getStatusIconClass(true) })
  }
  if (valetParking) {
    const available = isYesValue(venue.value.valetParking)
    rows.push({
      label: available ? 'Valet Parking available' : 'Valet parking not available',
      value: valetParking,
      available,
      iconClass: getStatusIconClass(available)
    })
  }

  return rows
})
const alcoholPolicySummary = computed(() => {
  const items = [
    formatStatusPolicyValue(policies.value.alcoholInhouse, 'In-house bar / alcohol available'),
    formatStatusPolicyValue(policies.value.alcoholOutside, 'Outside alcohol permitted')
  ].filter(Boolean)

  return items.join(' · ') || 'Alcohol policy not specified.'
})
const venueTimingRows = computed(() => {
  const sessionByType = (type) => (Array.isArray(venue.value.sessions) ? venue.value.sessions : []).find((session) => session.type === type)
  const morningSession = sessionByType('morning')
  const eveningSession = sessionByType('evening')
  const requestSession = sessionByType('on-request')

  return [
    {
      key: 'morning',
      label: 'Morning',
      available: Boolean(morningSession || venue.value.morningTimingFrom || venue.value.morningTimingTo),
      start: morningSession?.fromTime || venue.value.morningTimingFrom || '',
      end: morningSession?.toTime || venue.value.morningTimingTo || ''
    },
    {
      key: 'evening',
      label: 'Evening',
      available: Boolean(eveningSession || venue.value.eveningTimingFrom || venue.value.eveningTimingTo),
      start: eveningSession?.fromTime || venue.value.eveningTimingFrom || '',
      end: eveningSession?.toTime || venue.value.eveningTimingTo || ''
    },
    {
      key: 'on-request',
      label: 'On-Request',
      available: Boolean(requestSession),
      start: requestSession ? 'On request' : '',
      end: requestSession ? 'On request' : ''
    }
  ]
})
const hasTransportationDetails = computed(() => nearbyAreasList.value.length > 0 || Boolean(transportationMapLink.value))
const menuPricingUnitLabel = computed(() => policies.value.cateringPriceUnit || 'Per Person')
const menuPricingItems = computed(() => {
  const foodType = String(policies.value.cateringFoodType || '').trim()
  const pricingCount = String(policies.value.pricingBasisCount || '').trim()
  const nonVegPricingCount = String(policies.value.nonVegPricingBasisCount || '').trim()
  const normalizedFoodType = foodType.toLowerCase()
  const bothPricing = parseBothFoodPricing(pricingCount)
  const hasBoth = normalizedFoodType.includes('both')
  const rows = []

  if (hasBoth) {
    if (bothPricing.veg || pricingCount) rows.push({ name: 'Veg', rawPrice: bothPricing.veg || pricingCount, swatchBorderClass: 'border border-[#1f9d45]', swatchClass: 'bg-[#1f9d45]' })
    if (bothPricing.nonVeg || nonVegPricingCount) rows.push({ name: 'Non Veg', rawPrice: bothPricing.nonVeg || nonVegPricingCount, swatchBorderClass: 'border border-[#d45711]', swatchClass: 'bg-[#d45711]' })
  } else if (normalizedFoodType.includes('non')) {
    rows.push({ name: 'Non Veg', rawPrice: pricingCount, swatchBorderClass: 'border border-[#d45711]', swatchClass: 'bg-[#d45711]' })
  } else if (normalizedFoodType.includes('veg')) {
    rows.push({ name: 'Veg', rawPrice: pricingCount, swatchBorderClass: 'border border-[#1f9d45]', swatchClass: 'bg-[#1f9d45]' })
  } else if (pricingCount) {
    rows.push({ name: 'Menu', rawPrice: pricingCount, swatchBorderClass: 'border border-[#b78969]', swatchClass: 'bg-[#b78969]' })
  }

  return rows
    .filter((item) => String(item.rawPrice || '').trim())
    .map((item) => ({
      ...item,
      price: formatCurrency(item.rawPrice) || String(item.rawPrice || '').trim()
    }))
})
const selectedSpace = computed(() => {
  if (!spaces.value.length) return null
  return spaces.value.find((item) => item.id === selectedSpaceId.value) || spaces.value[0]
})

const venueRuleItems = computed(() => [
  {
    icon: '🍽️',
    label: 'Catering',
    rows: [
      formatStatusPolicyRow(policies.value.inHouseCatering, 'In-house catering'),
      formatStatusPolicyRow(policies.value.outsideCatering, 'Outside catering')
    ].filter(Boolean),
    value: 'Not specified'
  },
  { icon: '🌸', label: 'Decoration', value: policies.value.decorPolicy || 'Not specified' },
  { icon: '🎵', label: 'DJ / Music', value: policies.value.musicPolicy || 'Not specified' },
  {
    icon: '🥂',
    label: 'Alcohol',
    rows: [
      formatStatusPolicyRow(policies.value.alcoholInhouse, 'In-house bar / alcohol available'),
      formatStatusPolicyRow(policies.value.alcoholOutside, 'Outside alcohol permitted')
    ].filter(Boolean),
    value: alcoholPolicySummary.value
  },
  { icon: '🔊', label: 'Noise curfew', value: policies.value.noiseMusic ? `Music must end by ${policies.value.noiseMusic}.` : 'Not specified' },
  { icon: '📋', label: 'House rules', value: policies.value.otherHouseRules || 'Not specified' }
])

const policyRuleItems = computed(() => venueRuleItems.value.filter((item) => item.label !== 'Catering'))
const cateringRestrictionRows = computed(() => venueRuleItems.value.find((item) => item.label === 'Catering')?.rows || [])

const bookingTermItems = computed(() => [
  { icon: '👥', label: 'Guest count', value: policies.value.minGuests || policies.value.maxGuests ? `${policies.value.minGuests || '0'} - ${policies.value.maxGuests || '0'} guests` : 'Not specified' },
  { icon: '🛠️', label: 'Setup time', value: policies.value.decorTime || 'Not specified' },
  { icon: '🧹', label: 'Cleanup time', value: policies.value.cleanTime || 'Not specified' },
  { icon: '💳', label: 'Deposit', value: policies.value.depositPercent ? `${policies.value.depositPercent}% of total` : 'Not specified' },
  { icon: '↩️', label: 'Cancellation', value: policies.value.cancelTerms || 'Not specified' }
])

const breadcrumbCategory = computed(() => venue.value.typeLabel || 'Venue')

const goBack = () => {
  if (typeof window !== 'undefined' && window.history.length > 1) {
    router.back()
    return
  }

  router.push({
    name: 'venues',
    query: {
      location: String(route.query.location || ''),
      lat: String(route.query.lat || route.query.latitude || ''),
      lng: String(route.query.lng || route.query.longitude || ''),
      type: 'venues'
    }
  })
}

const destroyInviteGallery = () => {
  if (inviteGalleryLightbox.value) {
    inviteGalleryLightbox.value.destroy(true)
    inviteGalleryLightbox.value = null
  }
}

const openInviteGallery = (startIndex = 0) => {
  if (!lightboxGalleryImages.value.length) return
  destroyInviteGallery()
  inviteGalleryLightbox.value = lightGallery(document.createElement('div'), {
    dynamic: true,
    dynamicEl: lightboxGalleryImages.value.map((image) => ({
      src: image.src,
      thumb: image.src
    })),
    index: Math.max(0, startIndex),
    plugins: [lgThumbnail, lgZoom],
    download: false,
    actualSize: false
  })
  inviteGalleryLightbox.value.openGallery(startIndex)
}

const openInviteGalleryById = (image) => {
  const imageId = String(image?.id || '')
  const startIndex = lightboxGalleryImages.value.findIndex((item) => String(item.id || '') === imageId)
  openInviteGallery(startIndex >= 0 ? startIndex : 0)
}

const redirectToNotFound = () => {
  router.replace({
    name: 'frontend-404',
    query: {
      from: 'venue',
      venue_id: String(route.params.venueId || '')
    }
  })
}

const submitVenueEnquiry = async () => {
  enquiryError.value = ''
  enquirySuccess.value = ''

  if (!enquiryForm.value.name || !enquiryForm.value.email || !enquiryForm.value.phone || !enquiryForm.value.details) {
    enquiryError.value = 'Name, email, phone, and requirements are required.'
    return
  }

  enquiryLoading.value = true
  try {
    const formData = new FormData()
    formData.append('venue_id', venue.value.id || String(route.params.venueId || ''))
    formData.append('venue_name', venue.value.title || '')
    formData.append('name', enquiryForm.value.name)
    formData.append('email', enquiryForm.value.email)
    formData.append('phone', enquiryForm.value.phone)
    formData.append('event_date', enquiryForm.value.eventDate || '')
    formData.append('guest_count', enquiryForm.value.guestCount || '')
    formData.append('description', enquiryForm.value.details)
    formData.append('contextual_categories', venue.value.typeLabel || 'Venue')
    const { data } = await api.sendEnquiry(formData)
    const isError = data?.status === 0 || data?.status === 'error' || data?.success === false
    if (isError) {
      throw new Error(data?.msg || data?.message || 'Unable to send enquiry.')
    }
    enquirySuccess.value = data?.message || data?.msg || 'Enquiry sent successfully.'
    enquiryForm.value.details = ''
    enquiryForm.value.eventDate = ''
    enquiryForm.value.guestCount = ''
  } catch (err) {
    enquiryError.value = err?.response?.data?.msg || err?.response?.data?.message || err?.message || 'Unable to send enquiry.'
  } finally {
    enquiryLoading.value = false
  }
}

const loadVenueDetail = async () => {
  loading.value = true
  error.value = ''
  try {
    const formData = new FormData()
    formData.append('venue_id', String(route.params.venueId || ''))
    const { data } = await api.getVenueDetailInvite(formData)
    const payload = data?.data || {}
    if (!payload || !Object.keys(payload).length) {
      redirectToNotFound()
      return
    }

    venue.value = {
      id: String(payload?.id || route.params.venueId || ''),
      title: payload?.title || 'Untitled venue',
      tagLine: payload?.tag_line || '',
      about: payload?.about || payload?.description || '',
      address: payload?.fulladdress || payload?.address || 'Location unavailable',
      phone: payload?.phone || payload?.mobile || payload?.contact_number || '',
      phoneCode: payload?.phone_code || '',
      email: payload?.email || '',
      website: payload?.website || '',
      typeLabel: payload?.type_name || payload?.type || payload?.venue_type || 'Venue',
      yearEstablished: payload?.est_year || payload?.year_established || '',
      highlights: splitToArray(payload?.highlights || payload?.venue_highlights),
      inHouseServices: splitToArray(payload?.in_house_services || payload?.inHouseServices),
      additionalFacilities: splitToArray(payload?.additional_facilities || payload?.additionalFacilities),
      additionalFacilitiesStatuses: splitToArray(payload?.additional_facilities_status || payload?.additionalFacilitiesStatus),
      noOfRooms: payload?.no_of_rooms || payload?.rooms_count || '',
      parkingAvailable: getFirstValue(payload?.car_parking_available, payload?.car_parking_avail, payload?.parking_available),
      valetParking: payload?.wallet_parking || payload?.valet_parking || '',
      parkingVehicleCount: getFirstValue(payload?.parking_vehicle_count, payload?.no_of_vehicles),
      morningTimingFrom: getFirstValue(payload?.venue_timing_morning_from, payload?.morning_timing_from, payload?.mrng_from_time),
      morningTimingTo: getFirstValue(payload?.venue_timing_morning_to, payload?.morning_timing_to, payload?.mrng_to_time),
      eveningTimingFrom: getFirstValue(payload?.venue_timing_evening_from, payload?.evening_timing_from, payload?.evening_from_time),
      eveningTimingTo: getFirstValue(payload?.venue_timing_evening_to, payload?.evening_timing_to, payload?.evening_to_time),
      sessions: buildVenueTimingSessions(payload),
      nearbyAreas: splitToArray(payload?.nearby_areas || payload?.transportation_nearby_areas),
      transportationMapLink: payload?.transportation_map_link || payload?.google_maps_link || payload?.map_link || ''
    }

    gallery.value = normalizeGallery(payload?.gallery_info || [])
    spaces.value = Array.isArray(payload?.spaces_info)
      ? payload.spaces_info.map((item, index) => normalizeSpace(item, index))
      : []
    selectedSpaceId.value = spaces.value[0]?.id || ''

    packages.value = Array.isArray(payload?.venue_packages)
      ? payload.venue_packages.map((item, index) => ({
          id: String(item?.id || `package-${index}`),
          name: item?.name || 'Untitled package',
          description: item?.description || '',
          maxGuests: item?.max_guests || '',
          validHours: item?.valid_hours || '',
          includedServices: splitToArray(item?.included_services),
          priceLabel: formatCurrency(item?.price) || 'Price on request'
        }))
      : []

    policies.value = {
      inHouseCatering: payload?.venue_policies?.in_house_catering || '',
      outsideCatering: payload?.venue_policies?.outside_catering || '',
      cateringFoodType: payload?.venue_policies?.catering_food_type || payload?.venue_policies?.food_type || payload?.venue_policies?.veg_non_veg || '',
      cateringPriceUnit: payload?.venue_policies?.catering_price_unit || payload?.venue_policies?.price_unit || payload?.venue_policies?.price_per || payload?.venue_policies?.pricing_basis || '',
      pricingBasisCount: payload?.venue_policies?.pricing_basis_count || payload?.venue_policies?.count || '',
      nonVegPricingBasisCount: payload?.venue_policies?.pricing_basis_count_nonveg || payload?.venue_policies?.nonveg_count || '',
      decorPolicy: payload?.venue_policies?.decor_policy || '',
      musicPolicy: payload?.venue_policies?.music_policy || '',
      alcoholInhouse: String(payload?.venue_policies?.alcohol_inhouse || ''),
      alcoholOutside: String(payload?.venue_policies?.alcohol_outside || ''),
      minGuests: payload?.venue_policies?.min_guests || '',
      maxGuests: payload?.venue_policies?.max_guests || '',
      decorTime: payload?.venue_policies?.decor_time || '',
      cleanTime: payload?.venue_policies?.clean_time || '',
      noiseMusic: payload?.venue_policies?.noise_music || '',
      otherHouseRules: payload?.venue_policies?.other_house_rules || '',
      cancelTerms: payload?.venue_policies?.cancel_terms || '',
      depositPercent: payload?.venue_policies?.deposit_percent || ''
    }

    if (data?.status !== 'ok' && data?.msg) {
      if (/not available|not found|invalid/i.test(String(data.msg || ''))) {
        redirectToNotFound()
        return
      }
      error.value = data.msg
    }
  } catch (err) {
    const message = err?.message || 'Failed to load venue details.'
    if (/not available|not found|invalid/i.test(String(message))) {
      redirectToNotFound()
      return
    }
    error.value = message
  } finally {
    loading.value = false
  }
}

onMounted(() => {
  loadVenueDetail()
})

onBeforeUnmount(() => {
  destroyInviteGallery()
})
</script>
